/**
 * Offline Storage Manager
 * Handles IndexedDB operations for offline data persistence
 */

const DB_NAME = 'FireGuardDB';
const DB_VERSION = 1;

// Object store names
const STORES = {
  OFFLINE_INSPECTIONS: 'offline-inspections',
  CACHED_DATA: 'cached-data',
  SYNC_QUEUE: 'sync-queue',
};

/**
 * Open IndexedDB database
 */
export function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create offline inspections store
      if (!db.objectStoreNames.contains(STORES.OFFLINE_INSPECTIONS)) {
        const inspectionsStore = db.createObjectStore(STORES.OFFLINE_INSPECTIONS, {
          keyPath: 'id',
          autoIncrement: true,
        });
        inspectionsStore.createIndex('timestamp', 'timestamp', { unique: false });
        inspectionsStore.createIndex('synced', 'synced', { unique: false });
      }

      // Create cached data store
      if (!db.objectStoreNames.contains(STORES.CACHED_DATA)) {
        const cachedDataStore = db.createObjectStore(STORES.CACHED_DATA, {
          keyPath: 'key',
        });
        cachedDataStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Create sync queue store
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncQueueStore = db.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        syncQueueStore.createIndex('type', 'type', { unique: false });
        syncQueueStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      console.log('IndexedDB initialized');
    };
  });
}

/**
 * Save inspection for offline sync
 */
export async function saveOfflineInspection(inspectionData: any): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.OFFLINE_INSPECTIONS], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_INSPECTIONS);

    const data = {
      ...inspectionData,
      timestamp: Date.now(),
      synced: false,
    };

    const request = store.add(data);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as number);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Get all offline inspections
 */
export async function getOfflineInspections(): Promise<any[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.OFFLINE_INSPECTIONS], 'readonly');
    const store = transaction.objectStore(STORES.OFFLINE_INSPECTIONS);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Get unsynced inspections count
 */
export async function getUnsyncedCount(): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.OFFLINE_INSPECTIONS], 'readonly');
    const store = transaction.objectStore(STORES.OFFLINE_INSPECTIONS);
    // Booleans are not valid IndexedDB keys, so the 'synced' index cannot be
    // queried with IDBKeyRange.only(false). Count by scanning the (small) store.
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve((request.result as Array<{ synced?: boolean }>).filter((r) => !r.synced).length);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Mark inspection as synced
 */
export async function markInspectionSynced(id: number): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.OFFLINE_INSPECTIONS], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_INSPECTIONS);
    const request = store.get(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const data = request.result;
      if (data) {
        data.synced = true;
        data.syncedAt = Date.now();
        store.put(data);
      }
      resolve();
    };

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Delete offline inspection
 */
export async function deleteOfflineInspection(id: number): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.OFFLINE_INSPECTIONS], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_INSPECTIONS);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Cache data for offline access
 */
export async function cacheData(key: string, data: any, ttl = 3600000): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.CACHED_DATA], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_DATA);

    const cacheEntry = {
      key,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };

    const request = store.put(cacheEntry);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Get cached data
 */
export async function getCachedData(key: string): Promise<any | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.CACHED_DATA], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_DATA);
    const request = store.get(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const result = request.result;

      // Check if cached data is expired
      if (result && result.expiresAt < Date.now()) {
        // Delete expired data
        const deleteTransaction = db.transaction([STORES.CACHED_DATA], 'readwrite');
        const deleteStore = deleteTransaction.objectStore(STORES.CACHED_DATA);
        deleteStore.delete(key);
        resolve(null);
      } else {
        resolve(result ? result.data : null);
      }
    };

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Clear all cached data
 */
export async function clearCache(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.CACHED_DATA], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_DATA);
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Clear expired cache entries
 */
export async function clearExpiredCache(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.CACHED_DATA], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_DATA);
    const request = store.openCursor();

    request.onerror = () => reject(request.error);
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        if (cursor.value.expiresAt < Date.now()) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(type: string, data: any): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);

    const queueItem = {
      type,
      data,
      timestamp: Date.now(),
      retries: 0,
    };

    const request = store.add(queueItem);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as number);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Get sync queue items
 */
export async function getSyncQueue(): Promise<any[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Remove item from sync queue
 */
export async function removeFromSyncQueue(id: number): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Check if online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Register online/offline event listeners
 */
export function registerNetworkListeners(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}
